import type { ReactNode } from 'react';

/**
 * Content available to assistive technology and not to sight.
 *
 * Deliberately not `display: none` and not `hidden`: both remove the element
 * from the accessibility tree, which is the opposite of the intent. The clip
 * technique keeps it announced while occupying no visual space.
 */

const STYLE = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

interface VisuallyHiddenProps {
  children: ReactNode;
  id?: string;
  /**
   * `div` where the hidden content is flow content rather than phrasing — a
   * Chart's table of its own values, say. A `<span>` may not contain a
   * `<table>`, and an invalid nesting is not made valid by being invisible.
   */
  as?: 'span' | 'div';
}

export function VisuallyHidden({ children, id, as: Component = 'span' }: VisuallyHiddenProps) {
  return (
    <Component id={id} style={STYLE}>
      {children}
    </Component>
  );
}
